import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import styles from './pager.scss?inline'

/**
 * `dap-ds-pager`
 * @summary A pager is a component that displays pagination controls.
 * @element dap-ds-pager
 * @title - Pager
 *
 * @event {{ action: string, pagination: { pageIndex: number, pageSize: number } }} dds-pagination-change - Event fired when the pagination changes
 *
 * @csspart base - The main pager container.
 * @csspart first - The first page button.
 * @csspart previous - The previous page button.
 * @csspart next - The next page button.
 * @csspart last - The last page button.
 *
 * @cssproperty --dds-pager-spacing-vertical - The vertical spacing of the pager.
 * @cssproperty --dds-pager-spacing-horizontal - The horizontal spacing of the pager.
 * @cssproperty --dds-pager-button-padding - The padding of the pager buttons.
 * @cssproperty --dds-pager-button-border-width - The border width of the pager buttons.
 * @cssproperty --dds-pager-button-border-color - The border color of the pager buttons.
 * @cssproperty --dds-pager-button-border-radius - The border radius of the pager buttons.
 * @cssproperty --dds-pager-button-background - The background color of the pager buttons.
 * @cssproperty --dds-pager-button-font-size - The font size of the pager buttons.
 * @cssproperty --dds-pager-button-font-weight - The font weight of the pager buttons.
 */
@customElement('dap-ds-pager')
export default class DapDSPager extends DdsElement {
  /** Enable manual pagination. If true, the component will not automatically update the page index. */
  @property({ type: Boolean }) manualPagination = false
  /** The current page index. */
  @property({ type: Number }) pageIndex = 0
  @state() private _pageIndex = 0
  /** The number of items per page. */
  @property({ type: Number }) pageSize = 10
  @state() private _pageSize = 10
  /** The total number of rows. */
  @property({ type: Number }) totalRows = 0
  /** Available page size options */
  @property({ type: Array }) pageSizeOptions = [10, 25, 50, 100]

  /** Show the page size options. */
  @property({ type: String }) showPageSizeOptions = 'true'
  /** Show the page index. */
  @property({ type: String }) showPageIndex = 'true'
  /** Show the page count. */
  @property({ type: String }) showPageCount = 'true'
  /** Show the first button. */
  @property({ type: String }) showFirstButton = 'true'
  /** Show the previous button. */
  @property({ type: String }) showPreviousButton = 'true'
  /** Show the next button. */
  @property({ type: String }) showNextButton = 'true'
  /** Show the last button. */
  @property({ type: String }) showLastButton = 'true'

  /** The first button label. */
  @property({ type: String }) firstButtonLabel: string
  /** The previous button label. */
  @property({ type: String }) previousButtonLabel: string
  /** The next button label. */
  @property({ type: String }) nextButtonLabel: string
  /** The last button label. */
  @property({ type: String }) lastButtonLabel: string

  /** The function to determine the pager text
   * @type {Function}
   */
  @property({ attribute: false }) pageStateText = (
    pageIndex: number,
    pageSize: number,
    totalRows: number,
  ) => {
    return t('pager.pageInfo', {
      rangeStart: pageIndex * pageSize + 1,
      rangeEnd: (pageIndex + 1) * pageSize,
      totalRows: totalRows,
    })
  }

  static readonly styles = unsafeCSS(styles)

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (this.manualPagination) {
      if (_changedProperties.has('pageIndex')) {
        this._pageIndex = this.pageIndex
      }

      if (_changedProperties.has('pageSize')) {
        this._pageSize = this.pageSize
      }
    }
  }

  private updatePageIndex(action: string, pageIndex: number, pageSize: number) {
    this.manualPagination
      ? (this._pageIndex = pageIndex)
      : (this.pageIndex = pageIndex)

    this.emit('dds-pagination-change', {
      action,
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    })
  }

  private updatePageSize(pageSize: number) {
    // Reset to first page when changing page size
    const pageIndex = 0

    if (this.manualPagination) {
      this._pageSize = pageSize
      this._pageIndex = pageIndex
    } else {
      this.pageSize = pageSize
      this.pageIndex = pageIndex
    }

    this.emit('dds-pagination-change', {
      action: 'pageSize',
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    })
  }

  private renderPageSizeSelect() {
    if (this.showPageSizeOptions !== 'true') return nothing

    return html`<dap-ds-select
      class="pager__select"
      part="page-size"
      floatingStrategy="fixed"
      noAnimation
      size="xs"
      .value=${this.manualPagination
        ? this._pageSize.toString()
        : this.pageSize.toString()}
      @dds-change=${(e: Event) => {
        if ((e.target as HTMLSelectElement).value) {
          this.updatePageSize(Number((e.target as HTMLSelectElement).value))
        }
      }}>
      <dap-ds-icon-arrow-down-s-fill
        slot="indicator-icon"
        size="16"></dap-ds-icon-arrow-down-s-fill>
      ${this.pageSizeOptions && this.pageSizeOptions.length > 0
        ? this.pageSizeOptions.map(
            size =>
              html`<dap-ds-option-item
                value=${size}
                label=${t('pager.pageSize', { count: size })}
                >${size}</dap-ds-option-item
              >`,
          )
        : nothing}
    </dap-ds-select>`
  }

  private renderNavigationButton(
    part: string,
    action: string,
    pageIndex: number,
    disabled: boolean,
    label: string,
    slotName: string,
  ) {
    return html`<dap-ds-button
      part=${part}
      size="xs"
      variant="subtle-neutral"
      @click=${() => this.updatePageIndex(action, pageIndex, this.pageSize)}
      ?disabled=${disabled}>
      <slot name=${slotName}> ${label} </slot>
    </dap-ds-button>`
  }

  private renderPageInfo() {
    if (this.showPageIndex !== 'true') return nothing

    return html`<span
      >${this.pageStateText &&
      this.pageStateText(
        this.manualPagination ? this._pageIndex : this.pageIndex,
        this.manualPagination ? this._pageSize : this.pageSize,
        this.totalRows,
      )}</span
    >`
  }

  protected render() {
    const isLastPage =
      this.pageIndex === Math.ceil(this.totalRows / this.pageSize) - 1

    return html`
      <div part="base" class="pager">
        ${this.renderPageSizeSelect()}
        ${this.showFirstButton === 'true'
          ? this.renderNavigationButton(
              'first',
              'first',
              0,
              this.pageIndex === 0,
              this.firstButtonLabel || t('pager.first'),
              'first-button-label',
            )
          : nothing}
        ${this.showPreviousButton === 'true'
          ? this.renderNavigationButton(
              'previous',
              'previous',
              this.pageIndex - 1,
              this.pageIndex === 0,
              this.previousButtonLabel || t('pager.previous'),
              'previous-button-label',
            )
          : nothing}
        ${this.renderPageInfo()}
        ${this.showNextButton === 'true'
          ? this.renderNavigationButton(
              'next',
              'next',
              this.pageIndex + 1,
              isLastPage,
              this.nextButtonLabel || t('pager.next'),
              'next-button-label',
            )
          : nothing}
        ${this.showLastButton === 'true'
          ? this.renderNavigationButton(
              'last',
              'last',
              Math.ceil(this.totalRows / this.pageSize) - 1,
              isLastPage,
              this.lastButtonLabel || t('pager.last'),
              'last-button-label',
            )
          : nothing}
      </div>
    `
  }
}
